import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, Download } from 'lucide-react';
import { invoicesApi, isPublicS3PdfUrl } from '../api/invoices.api';

export interface InvoicePdfActionsProps {
  invoiceId?: number;
  pdfUrl?: string | null;
  hasPdf?: boolean;
  compact?: boolean;
}

export function InvoicePdfActions({
  invoiceId,
  pdfUrl,
  compact = false,
}: InvoicePdfActionsProps) {
  const [resolvedPdfUrl, setResolvedPdfUrl] = useState<string | null>(
    isPublicS3PdfUrl(pdfUrl) ? pdfUrl! : null
  );

  useEffect(() => {
    setResolvedPdfUrl(isPublicS3PdfUrl(pdfUrl) ? pdfUrl! : null);
  }, [pdfUrl]);

  useEffect(() => {
    if (isPublicS3PdfUrl(pdfUrl) || !invoiceId) return;

    let cancelled = false;
    invoicesApi
      .resolvePdfUrl(invoiceId)
      .then((url) => {
        if (!cancelled && url) setResolvedPdfUrl(url);
      })
      .catch(() => {
        // Ignore — buttons stay disabled when no PDF is available.
      });

    return () => {
      cancelled = true;
    };
  }, [invoiceId, pdfUrl]);

  const canUsePublicPdf = !!resolvedPdfUrl;
  const canDownload = !!resolvedPdfUrl || !!invoiceId;

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const url = resolvedPdfUrl ?? (invoiceId ? await invoicesApi.resolvePdfUrl(invoiceId) : null);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invoice link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleDownload = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const url = resolvedPdfUrl ?? (invoiceId ? await invoicesApi.resolvePdfUrl(invoiceId) : null);
    if (url) {
      window.open(url, '_blank');
      return;
    }
    if (!invoiceId) return;
    try {
      await invoicesApi.openPDF(invoiceId);
    } catch {
      toast.error('Failed to open invoice PDF');
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={handleCopy}
          disabled={!canUsePublicPdf}
          className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
          title={canUsePublicPdf ? 'Copy invoice link' : 'PDF not synced yet'}
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!canDownload}
          className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
          title={canDownload ? 'Download PDF' : 'PDF not synced yet'}
        >
          <Download className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={handleCopy}
        disabled={!canUsePublicPdf}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
        title={canUsePublicPdf ? resolvedPdfUrl ?? undefined : 'PDF not synced yet'}
      >
        <Copy className="w-4 h-4" />
        Copy link
      </button>
      <button
        type="button"
        onClick={handleDownload}
        disabled={!canDownload}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Download className="w-4 h-4" />
        Download PDF
      </button>
    </div>
  );
}

export default InvoicePdfActions;
